import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useData } from '../../../contexts/DataContext';
import { useAuth } from '../../../contexts/AuthContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

const { FiArrowLeft, FiSend, FiHelpCircle, FiMessageSquare, FiAlertCircle, FiCheckCircle, FiInfo } = FiIcons;

const SupportPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { submitSupportTicket } = useData();

  const [formData, setFormData] = useState({
    subject: '',
    category: 'general',
    message: '',
    priority: 'medium'
  });

  const [status, setStatus] = useState('idle'); // idle, submitting, success, error
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMessage('');

    try {
      if (!user) throw new Error('Du må være logget inn for å sende forespørsel via portalen. Send e-post til support@hmsvarsling.no for generelle henvendelser.');

      await submitSupportTicket({
        ...formData,
        user_email: user.email,
        user_id: user.id || 'unknown',
        company_id: user.companyId || 'unknown'
      });

      setStatus('success');
      setFormData({ subject: '', category: 'general', message: '', priority: 'medium' });
    } catch (error) {
      console.error('Support submission error:', error);
      setStatus('error');
      setErrorMessage(error.message || 'Det oppstod en feil ved innsending. Vennligst prøv igjen senere.');
    }
  };

  // OPPDATERT NAVIGASJON: Går nå til forsiden for vanlige brukere
  const handleBack = () => {
    if (user?.role === 'superadmin') {
      navigate('/superadmin');
    } else {
      // Alle andre (inkludert bedriftsbrukere) sendes til forsiden
      navigate('/');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <button
          onClick={handleBack}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-8 transition-colors font-medium"
        >
          <SafeIcon icon={FiArrowLeft} className="mr-2" />
          {user?.role === 'superadmin' ? 'Tilbake til kontrollpanel' : 'Tilbake til forsiden'}
        </button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-lg overflow-hidden"
        >
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-8 py-6">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-white/10 rounded-lg">
                <SafeIcon icon={FiHelpCircle} className="text-white text-3xl" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">Kundesupport</h1>
                <p className="text-blue-100 mt-1">Vi er her for å hjelpe deg.</p>
              </div>
            </div>
          </div>

          <div className="p-8">
            {status === 'success' ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-12"
              >
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <SafeIcon icon={FiCheckCircle} className="text-green-600 text-4xl" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Takk for din henvendelse!</h2>
                <p className="text-gray-600 mb-8">
                  Vi har mottatt din melding og vil behandle den så snart som mulig.
                </p>
                <button
                  onClick={() => setStatus('idle')}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Send ny melding
                </button>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {status === 'error' && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center">
                    <SafeIcon icon={FiAlertCircle} className="mr-2" />
                    {errorMessage}
                  </div>
                )}

                {!user && (
                  <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded-lg flex items-start gap-3 text-sm">
                    <SafeIcon icon={FiInfo} className="mt-0.5 flex-shrink-0" />
                    <span>
                      Dette skjemaet er for innloggede brukere. Hvis du ikke har konto, vennligst send e-post direkte eller ring oss.
                    </span>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Kategori
                    </label>
                    <div className="relative">
                      <select
                        name="category"
                        value={formData.category}
                        onChange={handleChange}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
                      >
                        <option value="general">Generelt spørsmål</option>
                        <option value="technical">Teknisk problem</option>
                        <option value="billing">Faktura og abonnement</option>
                        <option value="hms">HMS og Varsling</option>
                        <option value="feature">Ønske om funksjonalitet</option>
                      </select>
                      <SafeIcon icon={FiMessageSquare} className="absolute left-3 top-3 text-gray-400" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Prioritet
                    </label>
                    <select
                      name="priority"
                      value={formData.priority}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="low">Lav</option>
                      <option value="medium">Normal</option>
                      <option value="high">Høy</option>
                      <option value="critical">Kritisk</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Emne *
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Kort beskrivelse av henvendelsen"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Melding *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="6"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Beskriv problemet eller spørsmålet ditt så detaljert som mulig..."
                  />
                </div>

                <div className="flex items-center justify-end pt-4">
                  <button
                    type="submit"
                    disabled={status === 'submitting' || !user}
                    className={`flex items-center space-x-2 bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 transition-all shadow-md hover:shadow-lg ${(status === 'submitting' || !user) ? 'opacity-70 cursor-not-allowed' : ''}`}
                  >
                    {status === 'submitting' ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        <span>Sender...</span>
                      </>
                    ) : (
                      <>
                        <SafeIcon icon={FiSend} />
                        <span>Send henvendelse</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>

          <div className="bg-gray-50 px-8 py-4 border-t border-gray-100">
            <p className="text-sm text-gray-500 text-center">
              Du kan også nå oss på telefon 40 55 19 89 (08:00 - 16:00) eller e-post support@hmsvarsling.no
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default SupportPage;